import type { ClientAppVariantVersionCheckResult } from '@DigitaleDoerfer/digitale-doerfer-api';
import {
  ClientExceptionEntityTypeEnum,
  ClientOnboardingStatusHomeGeoAreaSelectionStatusEnum,
  GetAllAvailableOrganizationsRequiredTagsEnum
} from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC, PropsWithChildren } from 'react';
import { useCallback, useEffect, useMemo } from 'react';

import { useGetGrapevineOnboardingStatus } from '@/features/api/hooks/grapevine-app/useGetGrapevineOnboardingStatus';
import { useGetOrganizations } from '@/features/api/hooks/grapevine-organization/useGetOrganizations';
import { useSendPostView } from '@/features/api/hooks/grapevine-post-events/useSendPostView';
import { useGetOwnGlobalRoleAssignments } from '@/features/api/hooks/participant/useGetOwnGlobalRoleAssignments';
import { useGetOnboardingStatus } from '@/features/api/hooks/shared-app/useGetOnboardingStatus';
import { useGetOnboardingStatusInitial } from '@/features/api/hooks/shared-app/useGetOnboardingStatusInitial';
import { useGetKeycloakProfile } from '@/features/authorization/hooks/useGetKeycloakProfile';
import type { AppStateContextValues } from '@/features/authorization/provider/useAppState';
import { AppStateContext } from '@/features/authorization/provider/useAppState';
import { useAuth } from '@/features/authorization/provider/useAuth';
import { useRegistrationStore } from '@/features/authorization/store/useRegistrationStore';
import { StateView } from '@/shared/components/state-view/StateView';
import { useAppStateEffect } from '@/shared/hooks/useAppStateEffect';
import { usePostViewStore } from '@/shared/store/usePostViewStore';
import { hasItems } from '@/shared/utils/array';
import { isEmptyOrNull } from '@/shared/utils/string';

export const AppStateProvider: FC<PropsWithChildren> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  // Query sets the data for legalTexts and features if user is not logged in
  const {
    data: initialOnboardingStatus,
    isLoading: isInitialOnboardingStatusLoading
  } = useGetOnboardingStatusInitial();

  // Query sets the data for own chats (+badge), conveniences and suggestionCategories if user is logged in
  useGetGrapevineOnboardingStatus();

  /**
   * Query sets the data for the following queries if user is logged in
   * features, ownPerson, selectedGeoAreas, homeGeoArea (for id), legalTextStatus and versionCheckResult
   */
  const {
    data: onboardingStatus,
    isLoading: isOnboardingStatusLoading,
    refetch: updateAppStates,
    isRefetching: isOnboardingStatusRefetching,
    error
  } = useGetOnboardingStatus();

  const hasProfile: boolean = Boolean(onboardingStatus?.ownPerson);

  const hasHomeGeoArea: boolean =
    Boolean(onboardingStatus?.homeGeoArea) &&
    onboardingStatus?.homeGeoAreaSelectionStatus ===
      ClientOnboardingStatusHomeGeoAreaSelectionStatusEnum.Valid &&
    hasItems(onboardingStatus.selectedGeoAreas);

  const hasAcceptedTerms: boolean = Boolean(
    onboardingStatus?.legalTextStatus?.allRequiredAccepted
  );

  const versionCheckResult: ClientAppVariantVersionCheckResult | undefined =
    (isAuthenticated && onboardingStatus?.versionCheckResult) ||
    initialOnboardingStatus?.versionCheckResult;

  const hasValidVersion: boolean = Boolean(versionCheckResult?.supported);

  const hasRequiredAction: boolean =
    hasProfile && (!hasHomeGeoArea || !hasAcceptedTerms);

  const canUseApp: boolean =
    !(
      isOnboardingStatusLoading ||
      isOnboardingStatusRefetching ||
      isInitialOnboardingStatusLoading
    ) &&
    !hasRequiredAction &&
    hasValidVersion &&
    hasProfile;

  const clearRegistrationStore = useRegistrationStore((state) => state.reset);

  useEffect(() => {
    if (canUseApp) {
      clearRegistrationStore();
    }
  }, [canUseApp, clearRegistrationStore]);

  const userId: string = onboardingStatus?.id ?? '';

  // User may have home geo area but no selected geo areas
  const homeGeoAreaId = onboardingStatus?.homeGeoArea?.id ?? '';

  const { data: authProfile, isLoading: isKeyCloakProfileLoading } =
    useGetKeycloakProfile();

  const isNotReachableError = useMemo(() => {
    if (error) {
      const { type, message } = error;
      console.error(
        '[APP_STATE_PROVIDER]: something went wrong when fetching onboarding status:',
        message,
        type
      );

      return type !== ClientExceptionEntityTypeEnum.PersonWithOauthIdNotFound;
    } else return false;
  }, [error]);

  // TODO: add error handling
  const { data: emergencyOrganizations } = useGetOrganizations([
    GetAllAvailableOrganizationsRequiredTagsEnum.EmergencyAid
  ]);

  // TODO: Add error handling
  const { data: globalRoles = [] } = useGetOwnGlobalRoleAssignments();

  const isEmergencyOrganization = useCallback(
    (id?: string) => {
      if (isEmptyOrNull(id) || !hasItems(emergencyOrganizations)) return false;

      return emergencyOrganizations?.some((o) => o.id === id);
    },
    [emergencyOrganizations]
  );

  // #region post views
  const getPostViewsSinceLastSent = usePostViewStore(
    (state) => state.getPostViewsSinceLastSent
  );
  const updateLastSent = usePostViewStore((state) => state.updateLastSent);
  const { mutate: sendPostViewsMutate } = useSendPostView();
  const sendPostViews = useCallback(() => {
    if (!isAuthenticated) return;
    const postViews = getPostViewsSinceLastSent();
    if (postViews.length === 0) {
      return;
    }
    sendPostViewsMutate({ postViews }, { onSuccess: updateLastSent });
  }, [
    getPostViewsSinceLastSent,
    isAuthenticated,
    sendPostViewsMutate,
    updateLastSent
  ]);

  useAppStateEffect({
    onBackground: sendPostViews
  });
  // #endregion post views

  const contextValues: AppStateContextValues = useMemo(
    () => ({
      userId,
      userEmail: authProfile?.email ?? '',
      userAuthFirstName: authProfile?.given_name,
      userAuthLastName: authProfile?.family_name,
      homeGeoAreaId,
      hasRequiredAction,
      canUseApp,
      hasProfile,
      hasHomeGeoArea,
      hasAcceptedTerms,
      hasValidVersion,
      versionCheckResult,
      isNotReachableError,
      isOnboardingStatusRefetching,
      updateAppStates,
      isEmergencyOrganization,
      globalRoles
    }),
    [
      userId,
      authProfile?.email,
      authProfile?.given_name,
      authProfile?.family_name,
      homeGeoAreaId,
      hasRequiredAction,
      canUseApp,
      hasProfile,
      hasHomeGeoArea,
      hasAcceptedTerms,
      hasValidVersion,
      versionCheckResult,
      isNotReachableError,
      isOnboardingStatusRefetching,
      updateAppStates,
      isEmergencyOrganization,
      globalRoles
    ]
  );

  return (
    <AppStateContext.Provider value={contextValues}>
      <StateView
        asView
        isLoading={
          isOnboardingStatusLoading ||
          isInitialOnboardingStatusLoading ||
          isKeyCloakProfileLoading
        }
      >
        {children}
      </StateView>
    </AppStateContext.Provider>
  );
};
